import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import type { LocationCacheItem } from "../../types/session.ts";
import { useGridPermissions } from "../../hooks/usePermissions.ts";
import {
  getKioskKiosk,
  saveKiosk,
  getKioskInHouseStatuses,
  getKioskQrStaticGuid,
  saveKioskIdInInstantInventory,
  getTemperatureSettingData,
  setTemperatureSettingData,
  getActiveOpenHours,
  getAvailableOpenHourTypes,
  newOpenHourType,
  saveKioskOpenHour,
  saveOpenHourType,
  sendRestartKiosk,
  sendStatusCommand,
  mintRelayLink,
  type KioskKiosk,
  type TempIQBox,
  type TempSetting,
  type TempPayload,
  type OpenHourType,
} from "../../api/kiosk.ts";
import { QrCode } from "../../util/qrcodegen.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { formatWallClock, toDateInput } from "../../util/serverTime.ts";
import styles from "../../styles/formPage.module.scss";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import { KioskDrilldownTab } from "../segmentStatistics/kioskDrilldownTab.tsx";
import { KioskPricesTab } from "./kioskPricesTab.tsx";
import { KioskProblemsTab } from "./kioskProblemsTab.tsx";
import { KioskTemperatureChart } from "./kioskTemperatureChart.tsx";
import { getUpsStatus, type UpsStatusData } from "../../api/ups.ts";
import { LineChart, type LineSeries } from "../../components/chart/charts.tsx";
import { PagePopup } from "../../components/dataGrid/pagePopup.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { LocationSinglePicker } from "../../components/ui/locationSinglePicker.tsx";
import { CommonValueSelect } from "../../components/ui/commonValueSelect.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { useAssistant } from "../../config/assistantProfile.ts";
import { IssaQuickChat } from "../issaInsights/issaQuickChat.tsx";

/* ── helpers ──────────────────────────────────────────────── */

interface CommonCacheItem {
  TypeName?: string;
  Value?: string;
  Title?: string;
  sName?: string;
  sFilterValue?: string;
  sDisplayText?: string;
}

function emptyKiosk(): KioskKiosk {
  return {
    KioskId: -1,
    LocationID: -1,
    StoreName: "",
    StoreNumber: null,
    Name: null,
    City: "",
    Adress: "",
    ZipCode: "",
    Country: "",
    District: "",
    County: null,
    Chain: null,
    Type: "",
    KioskType: 0,
    Capacity: 0,
    Status: 0,
    InHouseStatus: "TEST",
    SerialNumber: null,
    IPNumber: null,
    ExternalReference: null,
    Placement: null,
    PlacementNotes: null,
    Position: null,
    Amplifier: 0,
    Washers: false,
    DropSlot: false,
    InnerDoorWindows: 0,
    Comments: null,
    TestString: null,
    sInstallDate: null,
    dInstallDate: null,
    RemovalDate: null,
    UPSReplace: null,
    BeltReplace: null,
    EjectRepair: null,
    RevenueRanking: 0,
    RevenueRankingData: "",
    SimPin: "",
    SimPuk: "",
    SimNumber: "",
    KioskDefined1: null,
    KioskDefined2: null,
    KioskDefined3: null,
    ExportStamp01: new Date().toISOString(),
    ExportStamp: new Date().toISOString(),
    TS: 0,
  };
}

/* ── page component ───────────────────────────────────────── */

export function EditKioskPage({ popupEditId, onClose, onSaved }: { popupEditId?: string; onClose?: () => void; onSaved?: () => void } = {}) {
  const [searchParams] = useSearchParams();
  // Local override for the Create→Edit transition after a successful Create.
  // `isCreate` reads this first; falls back to the URL/popup-derived value.
  const [createdEditId, setCreatedEditId] = useState<string | null>(null);
  const baseEditId = popupEditId ?? searchParams.get("edit");
  const editId = createdEditId ?? baseEditId;
  const isCreate = !editId;
  const navigate = useNavigate();
  const { cache, upsertKiosk, isSuperAdmin, canRead, canWrite } = useAuth();
  const { toast } = useToast();

  // "Problems" tab is gated on the ticketpage page-permission (separate from kiosk edit).
  const ticketPageRowId = useMemo(() => {
    const pages = cache?.PageInfo?.Page ?? [];
    return pages.find((p) => p.Name?.toLowerCase() === "ticketpage")?.RowID ?? -1;
  }, [cache]);
  const canReadTickets = ticketPageRowId > 0 && canRead(ticketPageRowId);
  const canWriteTickets = ticketPageRowId > 0 && canWrite(ticketPageRowId);

  // Permission check for tab grids
  const TAB_GRIDS = useMemo(() => [
    "accessInventorySlot", "productMinEdit", "InventorySuggestions",
    "kiosktemperaturesensor", "kioskOnlineStatus", "segmentKioskDrilldown",
    "KIOSKPRICEGRID",
  ], []);
  const gridPerms = useGridPermissions(TAB_GRIDS);

  const [kiosk, setKiosk] = useState<KioskKiosk>(emptyKiosk);
  // Pins to the LocationID as loaded from the backend so the summary line below
  // the picker always describes the saved location, even after the user changes
  // the picker selection without saving.
  const [originalLocationId, setOriginalLocationId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [tab, setTab] = useState<"edit" | "problems" | "qrcode" | "tempsettings" | "openhours" | "inventory" | "prices" | "stocking" | "analysis" | "connectivity" | "power" | "revenuedrivers">("edit");
  const [tabLoading, setTabLoading] = useState(false);
  const tabReadyRef = useRef(false);
  const markTabReady = useCallback(() => { if (!tabReadyRef.current) { tabReadyRef.current = true; setTabLoading(false); } }, []);
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(["edit"]));

  // Tabs that are always mounted don't need data loading
  const instantTabs = new Set(["edit", "qrcode", "openhours", "stocking"]);
  const [quantity, setQuantity] = useState(1);
  const [restartOpen, setRestartOpen] = useState(false);
  const [restartStatus, setRestartStatus] = useState<string | null>(null);
  const [vncBusy, setVncBusy] = useState(false);
  // Gated DB feature flag — hidden until this DB's kiosks are migrated to the relay.
  const relayVncEnabled = getDbFeatureFlags(cache).KioskRelayVnc === true;
  const [locationPopupPath, setLocationPopupPath] = useState<string | null>(null);

  // Load kiosk data + statuses
  // Dedupe by (editId, isCreate) — StrictMode double-mount would fire
  // getKioskInHouseStatuses + getKioskKiosk twice on first paint.
  const lastLoadKioskKeyRef = useRef<string>("");
  useEffect(() => {
    const dedupeKey = `${editId ?? ""}|${isCreate}`;
    if (lastLoadKioskKeyRef.current === dedupeKey) return;
    lastLoadKioskKeyRef.current = dedupeKey;
    getKioskInHouseStatuses()
      .then((r) => {
        if (!r.bError && r.oData) setStatuses(r.oData);
      })
      .catch(() => {});

    if (!isCreate) {
      setLoading(true);
      getKioskKiosk(Number(editId))
        .then((r) => {
          if (!r.bError && r.oData) {
            setKiosk(r.oData);
            setOriginalLocationId(r.oData.LocationID);
          } else {
            setStatus({ type: "error", message: r.sMessage ?? "Failed to load kiosk" });
          }
        })
        .catch((e: unknown) => setStatus({ type: "error", message: String(e) }))
        .finally(() => setLoading(false));
    }
  }, [editId, isCreate]);

  // Kiosk type options from cache
  const typeOptions = useMemo(() => {
    const common = (cache?.CommonData?.Common ?? []) as CommonCacheItem[];
    return common
      .filter((c) => (c.TypeName ?? c.sName ?? "").toLowerCase() === "skiosktype")
      .map((c) => c.Title ?? c.sDisplayText ?? c.Value ?? c.sFilterValue ?? "")
      .filter(Boolean)
      .sort();
  }, [cache]);

  // Info block describing the *saved* location (not the picker's current
  // selection), shown under the picker as read-only reference while editing.
  const originalLocation = useMemo(() => {
    if (originalLocationId == null || originalLocationId < 1) return null;
    const locs = cache?.CommonData?.Location ?? [];
    return locs.find((l) => l.LocationID === originalLocationId) ?? null;
  }, [originalLocationId, cache]);


  // Analysis tab: resolve kiosk's segment from location cache
  const analysisSegmentId = useMemo(() => {
    const locs = cache?.CommonData?.Location ?? [];
    return locs.find(l => l.LocationID === kiosk.LocationID)?.SegmentID ?? 0;
  }, [cache, kiosk.LocationID]);

  const analysisSegmentName = useMemo(() => {
    const names: Record<number, string> = { 9: "Airport", 13: "Education", 10: "Hospital", 11: "Hotel", 6: "Industry", 7: "Office", 2: "Other", 5: "Retail", 12: "Sport Center", 3: "Stadium", 8: "Transit Hub", 4: "Workplace", 0: "Unknown" };
    return names[analysisSegmentId] ?? "Unknown";
  }, [analysisSegmentId]);

  const set = <K extends keyof KioskKiosk>(key: K, val: KioskKiosk[K]) =>
    setKiosk((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    if (!kiosk.Type) {
      setStatus({ type: "error", message: "You need to select a Type for the Kiosk." });
      return;
    }
    if ((!isCreate || quantity <= 1) && !kiosk.ExternalReference) {
      setStatus({ type: "error", message: "You need to enter an External Reference for the Kiosk." });
      return;
    }
    if (kiosk.LocationID < 1) {
      setStatus({ type: "error", message: "You need to select a Location for the Kiosk." });
      return;
    }

    // Reject SQL-comment and statement-separator characters in text fields.
    const textFields: { label: string; value: string | null }[] = [
      { label: "External Reference", value: kiosk.ExternalReference },
      { label: "Serial Number", value: kiosk.SerialNumber },
      { label: "Placement Notes", value: kiosk.PlacementNotes },
      { label: "Type", value: kiosk.Type },
    ];
    for (const field of textFields) {
      if (field.value && (field.value.includes("--") || field.value.includes(";"))) {
        setStatus({ type: "error", message: `The characters "--" and ";" are not allowed in ${field.label}.` });
        return;
      }
    }

    if (!kiosk.Capacity) kiosk.Capacity = 0;

    setSaving(true);
    setStatus(null);
    try {
      const result = await saveKiosk(kiosk, isCreate ? quantity : 1);
      if (!result.bError && result.oData && result.oData > 0) {
        // Persist serial to InstantInventory
        if (kiosk.SerialNumber) {
          await saveKioskIdInInstantInventory(result.oData, kiosk.SerialNumber).catch(() => {});
        }
        // Splice into local cache for dropdowns/lookups. Bulk-create only updates
        // for the first new ID — the rest will land on the next cache delta.
        const newId = result.oData;
        const prior = cache?.CommonData?.Kiosk?.find((k) => k.KioskID === newId);
        // OwnerOperatorID isn't in the kiosk form — preserve from prior cache row
        // on edit, derive from the linked Location on create. Server is authoritative
        // and any drift self-heals on next cache delta.
        const locOwner = cache?.CommonData?.Location?.find((l) => l.LocationID === kiosk.LocationID)?.OwnerOperatorID;
        upsertKiosk({
          KioskID: newId,
          LocationID: kiosk.LocationID,
          StoreName: kiosk.StoreName,
          OwnerOperatorID: prior?.OwnerOperatorID ?? locOwner ?? 0,
          Address: kiosk.Adress,
          City: kiosk.City,
          District: kiosk.District,
          SerialNumber: kiosk.SerialNumber ?? undefined,
          IpAddress: kiosk.IPNumber ?? undefined,
          ExternalReference: kiosk.ExternalReference ?? undefined,
          Type: kiosk.Type,
          LiveStatus: prior?.LiveStatus,
          LastActivity: prior?.LastActivity,
          TestString: kiosk.TestString ?? undefined,
        });

        // Notify parent (popup) that data changed; grid refreshes on user close.
        onSaved?.();
        // Toast as a secondary cue (in case user already moved off the modal).
        toast(isCreate ? "Kiosk created" : "Kiosk saved", "success");

        if (isCreate) {
          // In-place Create→Edit transition. KEEP the modal mounted: just
          // promote the local kiosk to its new ID and let `isCreate` derive
          // false via `createdEditId`. Silent re-fetch fills in any
          // server-defaulted fields without flashing a "Loading…" body.
          setKiosk((prev) => ({ ...prev, KioskId: newId }));
          // Pre-prime the load-effect dedupe key so the upcoming
          // editId/isCreate flip doesn't re-fire it (would trigger
          // setLoading(true) and flash the body to "Loading...").
          lastLoadKioskKeyRef.current = `${newId}|false`;
          setCreatedEditId(String(newId));
          setOriginalLocationId(kiosk.LocationID);
          setStatus({ type: "success", message: `Saved — Kiosk #${newId}` });
          // Route-mode only: sync URL without remounting React Router routes.
          if (!onClose && typeof window !== "undefined") {
            window.history.replaceState({}, "", `/page/editkiosk?edit=${newId}`);
          }
          // Silent rehydrate from server (no loading=true wipe).
          getKioskKiosk(newId)
            .then((r) => { if (!r.bError && r.oData) setKiosk(r.oData); })
            .catch(() => {});
        } else {
          // Edit-mode save: keep modal open, show inline success banner.
          // User closes when ready; grid refreshes on close via dirty flag.
          setStatus({ type: "success", message: `Saved — Kiosk #${newId}` });
        }
      } else {
        setStatus({ type: "error", message: result.sMessage ?? "Could not save kiosk." });
      }
    } catch (e: unknown) {
      setStatus({ type: "error", message: String(e) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.formContainer}>Loading...</div>;

  const tabs = isCreate
    ? [{ key: "edit" as const, label: "Info" }]
    : [
        { key: "edit" as const, label: "Info" },
        { key: "inventory" as const, label: "Inventory" },
        ...(gridPerms["kioskpricegrid"] ? [{ key: "prices" as const, label: "Prices" }] : []),
        { key: "stocking" as const, label: "Stocking" },
        { key: "analysis" as const, label: "Analysis" },
        { key: "revenuedrivers" as const, label: "Revenue drivers" },
        { key: "tempsettings" as const, label: "Temperature" },
        { key: "connectivity" as const, label: "Connectivity" },
        { key: "power" as const, label: "Power" },
        { key: "openhours" as const, label: "Active Hours" },
        { key: "qrcode" as const, label: "QR Code" },
        ...(canReadTickets ? [{ key: "problems" as const, label: "Tickets" }] : []),
      ];

  /* ── Unified Modal (route + popup entry) ───────────────
     Route entry (/page/editkiosk?edit=X) and PagePopup entry both render the
     same Modal. Save/Cancel + auxiliaries (View Location, Restart) live in
     ModalFooter per AGENTS.md § Create/Edit Modal Guidelines. Closing the
     Modal from the route falls back to `navigate(-1)`. */
  const closeModal = onClose ?? (() => navigate(-1));
  const title = isCreate ? "Create new kiosk" : `Edit Kiosk — ID ${kiosk.KioskId}`;

  // Open an InstantRelay SSO deep-link in a new tab. The tab is opened synchronously
  // (before the await) so the popup blocker allows it, then navigated once the link is
  // minted. The token is short-lived + fragment-borne — never log/persist r.oData.
  const openRelay = async (target: "kiosk" | "console") => {
    const win = window.open("about:blank", "_blank");
    setVncBusy(true);
    try {
      const r = await mintRelayLink(kiosk.KioskId, target);
      if (!r.bError && r.oData) {
        if (win) win.location.href = r.oData;
        else window.open(r.oData, "_blank");
      } else {
        if (win) win.close();
        setStatus({ type: "error", message: r.sMessage ?? (target === "console" ? "Could not open the relay console." : "Could not open VNC.") });
      }
    } catch {
      if (win) win.close();
      setStatus({ type: "error", message: target === "console" ? "Could not open the relay console." : "Could not open VNC." });
    } finally {
      setVncBusy(false);
    }
  };

  const footerLeft = !isCreate ? (
    <>
      {kiosk.LocationID > 0 && (
        <button
          className={styles.secondaryBtn}
          onClick={() => setLocationPopupPath(`/page/editlocation?edit=${kiosk.LocationID}`)}
        >
          📍 View Location
        </button>
      )}
      <div style={{ position: "relative" }}>
        <button className={styles.dangerBtn} onClick={() => setRestartOpen((v) => !v)}>
          🔄 Restart
        </button>
        {restartOpen && (
          <div
            style={{
              position: "absolute", bottom: "100%", left: 0, marginBottom: 4,
              background: "var(--color-bg)", border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-1)", boxShadow: "var(--shadow-md)", zIndex: 100,
              display: "flex", flexDirection: "column", gap: 4, padding: 6, minWidth: 120,
            }}
          >
            <button
              className={styles.dangerBtn}
              onClick={async () => {
                setRestartOpen(false);
                try {
                  const rev = parseInt((/Revision (\d+)/i.exec(kiosk.TestString ?? "") ?? [])[1] ?? "0");
                  const r = rev > 33611
                    ? await sendStatusCommand("restartkiosk", kiosk.KioskId)
                    : await sendRestartKiosk(kiosk.KioskId);
                  setRestartStatus(!r.bError && r.oData
                    ? "Command sent. The kiosk should reboot within a few minutes."
                    : "Failed to write the restart command to the server.");
                } catch {
                  setRestartStatus("Failed to write the restart command to the server.");
                }
              }}
            >
              Kiosk
            </button>
            <button
              className={styles.secondaryBtn}
              onClick={async () => {
                setRestartOpen(false);
                try {
                  const rev = parseInt((/Revision (\d+)/i.exec(kiosk.TestString ?? "") ?? [])[1] ?? "0");
                  if (rev <= 33611) {
                    setRestartStatus("Kiosk does not support this feature.");
                    return;
                  }
                  const r = await sendStatusCommand("restartvnc", kiosk.KioskId);
                  setRestartStatus(!r.bError && r.oData
                    ? "Command sent. VNC should reboot within a few minutes."
                    : "Failed to write the restart command to the server.");
                } catch {
                  setRestartStatus("Failed to write the restart command to the server.");
                }
              }}
            >
              VNC
            </button>
          </div>
        )}
      </div>
      {relayVncEnabled && (
        <button className={styles.secondaryBtn} disabled={vncBusy} onClick={() => openRelay("kiosk")}>
          🖥️ {vncBusy ? "Opening VNC…" : "Open VNC"}
        </button>
      )}
      {relayVncEnabled && isSuperAdmin && (
        <button className={styles.secondaryBtn} disabled={vncBusy} onClick={() => openRelay("console")}>
          🛠️ Open relay console
        </button>
      )}
    </>
  ) : undefined;

  return (
    <GridEditModal
      title={title}
      wide
      maxWidth="1400px"
      saving={saving}
      saveLabel={isCreate ? "Create" : "Save Kiosk"}
      savingLabel={isCreate ? "Creating..." : "Saving..."}
      statusMessage={status}
      minBodyHeight="520px"
      footerLeft={footerLeft}
      onClose={closeModal}
      onSave={handleSave}
    >
      <>
        {restartStatus && (
          <div className={`${styles.statusMsg} ${restartStatus.startsWith("Command sent") ? styles.statusSuccess : styles.statusError}`}
            style={{ marginBottom: "var(--space-2)" }}>
            {restartStatus}
          </div>
        )}

        {/* Tabs (only relevant when editing; create mode is a single-tab form) */}
        {!isCreate && (
          <div style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-4)", borderBottom: "1px solid var(--color-border-default)", overflowX: "auto", flexShrink: 0 }}>
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  if (t.key !== tab) {
                    tabReadyRef.current = false;
                    setTab(t.key);
                    setVisitedTabs(prev => { const next = new Set(prev); next.add(t.key); return next; });
                    if (instantTabs.has(t.key) || visitedTabs.has(t.key)) {
                      setTabLoading(false);
                    } else {
                      setTabLoading(true);
                    }
                  }
                }}
                style={{
                  padding: "var(--space-2) var(--space-4)",
                  border: "none",
                  borderBottom: tab === t.key ? "2px solid var(--color-primary)" : "2px solid transparent",
                  background: "none",
                  cursor: "pointer",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  fontWeight: tab === t.key ? "var(--font-semibold)" : "var(--font-normal)",
                  color: tab === t.key ? "var(--color-primary)" : "var(--color-text-secondary)",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Tab content */}
        {tabLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <div style={{ width: 28, height: 28, border: "3px solid var(--color-border-default)", borderTopColor: "var(--color-primary)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        <div style={{ display: tabLoading ? "none" : undefined }}>
          <div style={{ display: tab === "edit" ? undefined : "none" }}>
            <EditTab
              kiosk={kiosk}
              set={set}
              statuses={statuses}
              typeOptions={typeOptions}
              originalLocation={originalLocation}
              isCreate={isCreate}
              quantity={quantity}
              onQuantityChange={setQuantity}
            />
          </div>
          <div style={{ display: tab === "problems" ? undefined : "none" }}>
            {visitedTabs.has("problems") && canReadTickets && (
              <KioskProblemsTab kioskId={kiosk.KioskId} canWrite={canWriteTickets} onReady={markTabReady} />
            )}
          </div>
          <div style={{ display: tab === "qrcode" ? undefined : "none" }}>
            <QrCodeTab kiosk={kiosk} />
          </div>
          <div style={{ display: tab === "tempsettings" ? undefined : "none" }}>
            <TemperatureSettingsTab kioskId={kiosk.KioskId} />
            {visitedTabs.has("tempsettings") && (<>
              <h3 style={{ margin: "var(--space-0) 0 var(--space-2)", fontWeight: "bold" }}>Temperature Sensor</h3>
              {gridPerms["kiosktemperaturesensor"] ? (
                <KioskTemperatureChart kioskId={kiosk.KioskId} onReady={markTabReady} />
              ) : <NoPermission />}
            </>)}
          </div>
          <div style={{ display: tab === "openhours" ? undefined : "none" }}>
            <ActiveHoursTab kioskId={kiosk.KioskId} />
          </div>
          <div style={{ display: tab === "connectivity" ? undefined : "none" }}>
            {visitedTabs.has("connectivity") && (
              gridPerms["kioskonlinestatus"] ? (
                <DataGrid
                  key={`connectivity-${kiosk.KioskId}`}
                  name="kioskOnlineStatus"
                  staticFilters={[{ sColumn: "sKiosks", oValue: String(kiosk.KioskId), Type: 8 }]}
                  hiddenFilters={["sKiosks"]}
                  autoSearch
                  hideToolbar
                  chartOnly
                  chartShowLabels={false}
                  onDataLoaded={markTabReady}
                />
              ) : <NoPermission />
            )}
          </div>
          <div style={{ display: tab === "power" ? undefined : "none" }}>
            {visitedTabs.has("power") && (
              <UpsPowerTab kioskId={kiosk.KioskId} onReady={markTabReady} />
            )}
          </div>
          <div style={{ display: tab === "revenuedrivers" ? undefined : "none" }}>
            {visitedTabs.has("revenuedrivers") && (
              gridPerms["inventorysuggestions"] ? (<>
                <IssaSuggestionsButton kioskId={kiosk.KioskId} />
                <h3 style={{ margin: "var(--space-4) 0 var(--space-4)" }} className={styles.sectionTitle}>Inventory Suggestions</h3>
                <DataGrid
                  key={`revenuedrivers-${kiosk.KioskId}`}
                  name="InventorySuggestions"
                  staticFilters={[{ sColumn: "KioskID", oValue: String(kiosk.KioskId) }]}
                  hiddenFilters={["KioskID"]}
                  hiddenColumns={["ExternalKioskReference","KioskID","sCity","sChain","sAddress","sStoreName","sAdress","OperatorName","SlotID","ExternalItemID","ProductID","iFilterStatus"]}
                  autoSearch
                  hideToolbar
                  onDataLoaded={markTabReady}
                />
              </>) : <NoPermission />
            )}
          </div>
          <div style={{ display: tab === "inventory" ? undefined : "none" }}>
            {visitedTabs.has("inventory") && (
              gridPerms["accessinventoryslot"] ? (<>
                <h3 className={styles.sectionTitle}>Critical Improvements</h3>
                <DataGrid
                  key={`weightcheck-${kiosk.KioskId}`}
                  name="inventoryWeightCheck"
                  staticFilters={[{ sColumn: "KioskID", oValue: String(kiosk.KioskId) }]}
                  hiddenFilters={["KioskID", "OperatorName", "ExternalItemID"]}
                  hiddenColumns={["KioskID", "sCity", "sStoreName", "sAdress", "OperatorName", "SlotID", "ExternalItemID", "ProductID", "iFilterStatus"]}
                  autoSearch
                  hideToolbar
                  onDataLoaded={markTabReady}
                />
                <h3 className={styles.sectionTitle}>Current Inventory</h3>
                <DataGrid
                  key={`inventory-${kiosk.KioskId}`}
                  name="accessInventorySlot"
                  staticFilters={[{ sColumn: "KioskID", oValue: String(kiosk.KioskId) }]}
                  hiddenFilters={["KioskID", "iLocationID"]}
                  hiddenColumns={["KioskInfo", "KioskID", "sChain", "iLocationID", "sStatus", "PositionLastChanged", "ProductLastChanged", "LastAction", "iFilterStatus", "iPrice"]}
                  autoSearch
                  hideToolbar
                />
              </>) : <NoPermission />
            )}
          </div>
          {gridPerms["kioskpricegrid"] && (
            <div style={{ display: tab === "prices" ? undefined : "none" }}>
              {visitedTabs.has("prices") && (
                <KioskPricesTab kioskId={kiosk.KioskId} onReady={markTabReady} />
              )}
            </div>
          )}
          <div style={{ display: tab === "stocking" ? undefined : "none" }}>
            <h3 className={styles.sectionTitle}>Restocking information and Suggestions</h3>
            {gridPerms["productminedit"] ? (
              <DataGrid
                key={`stocking-${kiosk.KioskId}`}
                name="productMinEdit"
                staticFilters={[{ sColumn: "KioskID", oValue: String(kiosk.KioskId) }]}
                hiddenFilters={["KioskID"]}
                hiddenColumns={["KioskID", "btnColumnProductLock", "ProductArticleNumber", "ProductBarcode","TotalRestock","Tax", "TotalPrice"]}
                autoSearch
                hideToolbar
              />
            ) : <NoPermission />}
          </div>
          <div style={{ display: tab === "analysis" ? undefined : "none" }}>
            {visitedTabs.has("analysis") && (
              gridPerms["segmentkioskdrilldown"] ? (<>
                <h3 className={styles.sectionTitle}>Kiosk comparison within same segment - {analysisSegmentName}</h3>
                <KioskDrilldownTab defaultSegmentId={analysisSegmentId} hiddenColumns={["sExternalReference", "sStoreName", "SegmentName"]} hiddenFilters={["KioskID", "sExternalReference"]} highlightColumn="KioskID" highlightValue={String(kiosk.KioskId)} onReady={markTabReady} />
              </>) : <NoPermission />
            )}
          </div>
        </div>

        {locationPopupPath && (
          <PagePopup path={locationPopupPath} onClose={() => setLocationPopupPath(null)} />
        )}
      </>
    </GridEditModal>
  );
}

/* ── No Permission Message ─────────────────────────────────── */

function NoPermission() {
  return (
    <div style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--color-text-muted)" }}>
      <p style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-medium)" }}>
        You do not have permission to see this data. Please contact admins.
      </p>
    </div>
  );
}

/* ── Edit Tab ─────────────────────────────────────────────── */

function EditTab({
  kiosk,
  set,
  statuses,
  typeOptions,
  originalLocation,
  isCreate,
  quantity,
  onQuantityChange,
}: {
  kiosk: KioskKiosk;
  set: <K extends keyof KioskKiosk>(key: K, val: KioskKiosk[K]) => void;
  statuses: string[];
  typeOptions: string[];
  /** Location as saved on the kiosk (null for Create mode or if not in cache). */
  originalLocation: LocationCacheItem | null;
  isCreate: boolean;
  quantity: number;
  onQuantityChange: (val: number) => void;
}) {
  const { activeEndpoint } = useAuth();
  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>Kiosk Info</h3>

      {isCreate && (
        <div className={styles.fieldRow} style={{maxWidth: 100}}>
          <label>Kiosks to create</label>
          <input
            type="number"
            min={1}
            max={100}
            value={quantity}
            onChange={(e) => onQuantityChange(Math.min(100, Math.max(1, Number(e.target.value))))}
            className={styles.fieldInput}
            style={{ width: 70 }}
          />
        </div>
      )}

      <div className={styles.fieldRow}>
        <label>Type <span style={{ color: "var(--color-error)" }}>*</span></label>
        <CommonValueSelect
          className={styles.fieldCombo}
          column="sKioskType"
          listLabel="Kiosk Type"
          allowNone={false}
          value={kiosk.Type ?? ""}
          options={typeOptions}
          onChange={(v) => set("Type", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Kiosk Status</label>
        <select
          className={styles.fieldSelect}
          value={kiosk.InHouseStatus}
          onChange={(e) => set("InHouseStatus", e.target.value)}
        >
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
          {statuses.length === 0 && <option value={kiosk.InHouseStatus}>{kiosk.InHouseStatus}</option>}
        </select>
      </div>

      <div className={styles.fieldRow}>
        <label>Location <span style={{ color: "var(--color-error)" }}>*</span></label>
        <LocationSinglePicker
          value={kiosk.LocationID > 0 ? kiosk.LocationID : 0}
          onChange={(id) => set("LocationID", id > 0 ? id : -1)}
        />
      </div>
      {!isCreate && <OriginalLocationInfo loc={originalLocation} currentId={kiosk.LocationID} />}

      {(!isCreate || quantity <= 1) && (
        <div className={styles.fieldRow}>
          <label>External Ref. <span style={{ color: "var(--color-error)" }}>*</span></label>

          <input
            className={styles.fieldInput}
            value={kiosk.ExternalReference ?? ""}
            onChange={(e) => set("ExternalReference", e.target.value)} />
            {activeEndpoint?.ShortName && (
              <span style={{ fontStyle: "italic", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>
                Installation prefix: <b>{activeEndpoint.ShortName}</b>
              </span>
            )}
        </div>
            

      )}

      <div className={styles.fieldRow}>
        <label>Capacity</label>
        <input
          type="number"
          className={styles.fieldInput}
          value={kiosk.Capacity}
          onChange={(e) => set("Capacity", Number(e.target.value))}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Serial Number</label>
        <input
          className={styles.fieldInput}
          value={kiosk.SerialNumber ?? ""}
          onChange={(e) => set("SerialNumber", e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Placement Notes</label>
        <input
          className={styles.fieldInput}
          value={kiosk.PlacementNotes ?? ""}
          onChange={(e) => set("PlacementNotes", e.target.value)}
        />
      </div>

      {!isCreate && (
        <>
          <div className={styles.fieldRow}>
            <label>IP Address</label>
            <input className={styles.fieldInput} value={kiosk.IPNumber ?? ""} readOnly />
          </div>

          {kiosk.TestString && (
            <div className={styles.fieldRow}>
              <label>Software Info</label>
              <span style={{ fontSize: "var(--text-sm)", fontStyle: "italic", color: "var(--color-text-secondary)" }}>
                {kiosk.TestString}
              </span>
            </div>
          )}
        </>
      )}

    </div>
  );
}

/* ── Saved-location info block ─────────────────────────────
   Summarises the location currently persisted on the kiosk (ignores the
   picker's unsaved selection). Callout format makes it obvious this is
   reference info, not a duplicate of the picker. */
function OriginalLocationInfo({ loc, currentId }: { loc: LocationCacheItem | null; currentId: number }) {
  if (!loc) {
    return (
      <div className={styles.hint} style={{ marginTop: "-4px" }}>
        No location saved for this kiosk yet.
      </div>
    );
  }
  const changed = currentId !== loc.LocationID;
  const addressLine = [loc.Address, loc.City, loc.District, loc.ZipCode, loc.Country].filter(Boolean).join(", ");
  return (
    <div
      style={{
        marginTop: "-4px",
        marginBottom: "var(--space-2)",
        padding: "var(--space-1-5) var(--space-2)",
        fontSize: "var(--text-xs)",
        color: "var(--color-text-secondary)",
        borderLeft: "2px solid var(--color-border-default)",
        background: "var(--color-bg-muted)",
        borderRadius: "0 var(--radius-1) var(--radius-1) 0",
      }}
    >
      <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontWeight: "var(--font-medium)", color: "var(--color-text)" }}>
          Saved: #{loc.LocationID} — {loc.StoreName || "(no name)"}
        </span>
        {changed && (
          <span style={{ color: "var(--color-warning)", fontStyle: "italic" }}>
            (unsaved change — picker shows a different location)
          </span>
        )}
      </div>
      {addressLine && (
        <div style={{ color: "var(--color-text-muted)", marginTop: 2 }}>{addressLine}</div>
      )}
    </div>
  );
}

/* ── QR Code Tab ──────────────────────────────────────────── */

function generateGuid(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]!.toString(16);
    hex += b.length < 2 ? "0" + b : b;
  }
  return hex;
}

function QrCodeTab({ kiosk }: { kiosk: KioskKiosk }) {
  const [prefix, setPrefix] = useState("https://lovaapp.com/download.html");
  const [confirmBtn, setConfirmBtn] = useState(false);
  const [clearCart, setClearCart] = useState(false);
  const [isSng, setIsSng] = useState(false);
  const [guidAutoGenerated, setGuidAutoGenerated] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrString, setQrString] = useState("");
  const guidRef = useRef<string | null>(null);

  const generateRef = useRef<(() => void) | null>(null);

  const handleGenerate = useCallback(() => {
    let currentGuid = guidRef.current ?? "EMPTY";
    let wasEmpty = currentGuid === "EMPTY";
    if (wasEmpty) {
      currentGuid = generateGuid();
      guidRef.current = currentGuid;
    }
    setGuidAutoGenerated(wasEmpty);

    const endpoint = 16;
    const flags =
      1 +
      (confirmBtn ? 8 : 0) +
      (isSng ? 0x40 : 0) +
      (clearCart ? 0x80 : 0) +
      endpoint;

    let info =
      "2|" +
      JSON.stringify({
        d: currentGuid,
        f: flags,
        t: JSON.stringify({ g: 0 }),
      });

    if (prefix) {
      info = prefix + "#" + info;
    }

    setQrString(info);

    // Generate QR code SVG and convert to PNG data URL
    const svg = QrCode.encodeText(info, QrCode.Ecc.HIGH).toSvgString(2);
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const canvas = document.createElement("canvas");
    const size = 400;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      setQrDataUrl(canvas.toDataURL("image/png"));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [prefix, confirmBtn, clearCart, isSng]);

  generateRef.current = handleGenerate;

  // Fetch GUID from server on mount and auto-generate QR
  // Dedupe by kioskId — StrictMode double-mount would fire getKioskQrStaticGuid twice.
  const lastQrLoadIdRef = useRef<number>(-1);
  useEffect(() => {
    if (kiosk.KioskId < 1) return;
    if (lastQrLoadIdRef.current === kiosk.KioskId) return;
    lastQrLoadIdRef.current = kiosk.KioskId;
    getKioskQrStaticGuid(kiosk.KioskId)
      .then((r) => {
        if (!r.bError && r.oData) {
          guidRef.current = r.oData;
          generateRef.current?.();
        }
      })
      .catch(() => {});
  }, [kiosk.KioskId]);

  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>QR Code</h3>

      <div className={styles.fieldRow}>
        <label>Website Prefix</label>
        <input
          className={styles.fieldInput}
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Clear Cart button in Lova</label>
        <input type="checkbox" checked={clearCart} onChange={(e) => setClearCart(e.target.checked)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Confirm button in Lova</label>
        <input type="checkbox" checked={confirmBtn} onChange={(e) => setConfirmBtn(e.target.checked)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Is SNG (snack market)</label>
        <input type="checkbox" checked={isSng} onChange={(e) => setIsSng(e.target.checked)} />
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--space-3)", marginBottom: "var(--space-3)" }}>
        <button className={styles.dangerBtn} onClick={handleGenerate}>
          Regenerate Kiosk QR
        </button>
      </div>

      {qrDataUrl && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-3)" }}>
          <img src={qrDataUrl} alt="Kiosk QR Code" width={400} height={400} />
        </div>
      )}

      {qrString && (
        <div className={styles.fieldRow}>
          <label>Generated QR String</label>
          <input className={styles.fieldInput} value={qrString} readOnly />
        </div>
      )}

      {guidAutoGenerated && (
        <p style={{ color: "green", fontSize: "var(--text-sm)", textAlign: "center" }}>
          QR guid was auto-generated because the kiosk has not been online.
        </p>
      )}
    </div>
  );
}

/* ── Temperature Settings Tab ─────────────────────────────── */

function TemperatureSettingsTab({ kioskId }: { kioskId: number }) {
  const [iqSettings, setIqSettings] = useState<TempIQBox[]>([]);
  const [kioskSettings, setKioskSettings] = useState<TempSetting[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (kioskId < 1) return;
    getTemperatureSettingData(kioskId)
      .then((r) => {
        if (!r.bError && r.oData) {
          setIqSettings(r.oData.IqLevelSettings ?? []);
          setKioskSettings(r.oData.KioskLevelSettings ?? []);
          if (r.oData.Message) setMessage(r.oData.Message);
        } else {
          setStatus("Failed to load temperature settings");
        }
      })
      .catch(() => setStatus("Failed to load temperature settings"));
  }, [kioskId]);

  const updateIq = (index: number, field: keyof TempIQBox, value: string) => {
    setIqSettings((prev) => {
      const arr = [...prev];
      const item = { ...arr[index]! };
      if (field === "TemperatureValue" || field === "TemperatureAlarmDiff") {
        (item as Record<string, unknown>)[field] = value;
      } else if (field === "TempSensorOfflineEnabled") {
        item.TempSensorOfflineEnabled = !item.TempSensorOfflineEnabled;
      }
      arr[index] = item;
      setStatus(null);
      return arr;
    });
  };

  const validateIq = (index: number, field: "TemperatureValue" | "TemperatureAlarmDiff") => {
    setIqSettings((prev) => {
      const arr = [...prev];
      const item = { ...arr[index]! };
      const raw = String((item as Record<string, unknown>)[field] ?? "");
      if (raw.includes(",")) {
        setStatus("No commas — use . instead");
        (item as Record<string, unknown>)[field] = 0;
        arr[index] = item;
        return arr;
      }
      const parsed = parseInt(raw, 10);
      if (isNaN(parsed)) {
        setStatus("Value needs to be an integral number");
        (item as Record<string, unknown>)[field] = 0;
        arr[index] = item;
        return arr;
      }
      (item as Record<string, unknown>)[field] = parsed;
      arr[index] = item;
      return arr;
    });
  };

  const updateKiosk = (index: number, value: string) => {
    setKioskSettings((prev) => {
      const arr = [...prev];
      const item = { ...arr[index]! };
      const key = item.sKey;

      if (key === "TemperatureControl") {
        setStatus("You can not change this value, must be done at machine");
        return prev;
      }
      item.sValue = value;
      arr[index] = item;
      setStatus(null);
      return arr;
    });
  };

  const validateKiosk = (index: number) => {
    setKioskSettings((prev) => {
      const arr = [...prev];
      const item = { ...arr[index]! };
      const key = item.sKey;

      if (key === "BadTemperatureDuration") {
        const parsed = parseInt(item.sValue, 10);
        if (isNaN(parsed) || item.sValue.trim() === "") {
          setStatus("Value needs to be an integral number");
          item.sValue = "0";
          arr[index] = item;
          return arr;
        }
        item.sValue = String(parsed);
        arr[index] = item;
      }
      return arr;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    const payload: TempPayload = {
      IqLevelSettings: iqSettings,
      KioskLevelSettings: kioskSettings,
      Message: "",
    };
    try {
      const r = await setTemperatureSettingData(kioskId, payload);
      if (!r.bError && r.oData) {
        setStatus("Successfully saved temperature settings");
      } else {
        setStatus("Failed to save temperature settings");
      }
    } catch {
      setStatus("Failed to save temperature settings");
    } finally {
      setSaving(false);
    }
  };

  if (kioskId < 1) {
    return <div className={styles.hint}>Save the kiosk first to manage temperature settings.</div>;
  }

  return (
    <div className={styles.formContainerWide}>
      {message && (
        <div className={`${styles.statusMsg} ${styles.statusError}`} style={{ marginBottom: "var(--space-3)" }}>
          {message}
        </div>
      )}

      {/* IQbox Level Settings */}
      <h3 className={styles.sectionTitle}>
        IQbox Level Settings <span style={{ fontWeight: "normal", fontStyle: "italic", fontSize: "var(--text-sm)" }}></span>
      </h3>
      {iqSettings.length > 0 ? (
        <div style={{ overflowX: "auto", marginBottom: "var(--space-4)" }}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Position</th>
                <th>Name</th>
                <th>IQ ID</th>
                <th>Desired temperature</th>
                <th>Alert on temp diff of</th>
                <th>Alert on sensor offline</th>
              </tr>
            </thead>
            <tbody>
              {iqSettings.map((iq, i) => (
                <tr key={iq.PiID}>
                  <td>{iq.Position}</td>
                  <td>{iq.Name}</td>
                  <td>{iq.PiID}</td>
                  <td>
                    <input
                      className={styles.fieldInput}
                      style={{ width: 100 }}
                      value={iq.TemperatureValue}
                      onChange={(e) => updateIq(i, "TemperatureValue", e.target.value)}
                      onBlur={() => validateIq(i, "TemperatureValue")}
                    />
                  </td>
                  <td>
                    <input
                      className={styles.fieldInput}
                      style={{ width: 100 }}
                      value={iq.TemperatureAlarmDiff}
                      onChange={(e) => updateIq(i, "TemperatureAlarmDiff", e.target.value)}
                      onBlur={() => validateIq(i, "TemperatureAlarmDiff")}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={iq.TempSensorOfflineEnabled}
                      onChange={() => updateIq(i, "TempSensorOfflineEnabled", "")}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.hint}>No IQ box settings found.</p>
      )}

      {/* Kiosk Level Settings */}
      <h3 className={styles.sectionTitle}>
        Kiosk Level Settings <span style={{ fontWeight: "normal", fontStyle: "italic", fontSize: "var(--text-sm)" }}></span>
      </h3>
      {kioskSettings.length > 0 ? (
        <div style={{ overflowX: "auto", marginBottom: "var(--space-0)" }}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Setting</th>
                <th>Text value</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {kioskSettings.map((s, i) => {
                const isBool = s.sKey === "KioskLockingEnabled" || s.sKey === "ShowTemperatureInFooter";
                const isReadOnly = s.sKey === "TemperatureControl";
                const isNumeric = s.sKey === "BadTemperatureDuration";
                return (
                  <tr key={s.sKey}>
                    <td>{s.sKey}</td>
                    <td>
                      {isBool ? (
                        <span style={{ display: "inline-flex", gap: "var(--space-3)", alignItems: "center" }}>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
                            <input
                              type="radio"
                              name={`kiosk-bool-${s.sKey}`}
                              checked={s.sValue.toLowerCase() === "true"}
                              onChange={() => updateKiosk(i, "True")}
                            />
                            Enable
                          </label>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
                            <input
                              type="radio"
                              name={`kiosk-bool-${s.sKey}`}
                              checked={s.sValue.toLowerCase() !== "true"}
                              onChange={() => updateKiosk(i, "False")}
                            />
                            Disable
                          </label>
                        </span>
                      ) : (
                        <input
                          className={styles.fieldInput}
                          style={{ width: 180 }}
                          value={s.sValue}
                          readOnly={isReadOnly}
                          onChange={(e) => updateKiosk(i, e.target.value)}
                          onBlur={isNumeric ? () => validateKiosk(i) : undefined}
                        />
                      )}
                    </td>
                    <td>{s.sDescription}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.hint}>No kiosk level settings found.</p>
      )}

      <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--space-3)" }}>
        <button
          className={styles.secondaryBtn}
          disabled={saving}
          onClick={handleSave}
          style={{ background: "var(--color-error)", color: "var(--color-error-foreground)", border: "none" }}
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {status && (
        <div
          className={`${styles.statusMsg} ${status.startsWith("Successfully") ? styles.statusSuccess : styles.statusError}`}
          style={{ marginTop: "var(--space-2)", textAlign: "center" }}
        >
          {status}
        </div>
      )}
    </div>
  );
}

/* ── Active Hours helpers ─────────────────────────────────── */

const DAY_OPTIONS = [
  { id: 1, label: "Monday" },
  { id: 2, label: "Tuesday" },
  { id: 3, label: "Wednesday" },
  { id: 4, label: "Thursday" },
  { id: 5, label: "Friday" },
  { id: 6, label: "Saturday" },
  { id: 7, label: "Sunday" },
  { id: 8, label: "Monday-Friday" },
  { id: 9, label: "Weekends" },
  { id: 100, label: "All Days" },
];

const TYPE_OPTIONS = [
  { id: 0, label: "Open Hours" },
  { id: 1, label: "Primary Door (external ware)" },
  { id: 2, label: "Main Store Mode" },
  { id: 3, label: "Energy Saver" },
];

const PRIO_OPTIONS = [
  { id: 1, label: "Normal" },
  { id: 2, label: "Medium" },
  { id: 3, label: "High" },
];

function getDayLabel(day: number): string {
  return DAY_OPTIONS.find((d) => d.id === day)?.label ?? String(day);
}

function getTypeLabel(type: number): string {
  return TYPE_OPTIONS.find((t) => t.id === type)?.label ?? String(type);
}

const ENERGY_SAVER_TYPE = 3;
const MAX_ENERGY_SAVER_TEMP_OFFSET = 10;

interface EnergySaverOptions {
  ledOff: boolean;
  lowerTemp: boolean;
  tempOffset: number;
  blackScreen: boolean;
}

const DEFAULT_ENERGY_SAVER: EnergySaverOptions = { ledOff: true, lowerTemp: false, tempOffset: 2, blackScreen: false };

function energySaverFromHour(h: OpenHourType): EnergySaverOptions {
  return {
    ledOff: h.EnergySaverLedOff ?? true,
    lowerTemp: h.EnergySaverLowerTemp ?? false,
    tempOffset: h.EnergySaverTempOffset || DEFAULT_ENERGY_SAVER.tempOffset,
    blackScreen: h.EnergySaverBlackScreen ?? false,
  };
}

function energySaverToHour(o: EnergySaverOptions): Pick<OpenHourType, "EnergySaverLedOff" | "EnergySaverLowerTemp" | "EnergySaverTempOffset" | "EnergySaverBlackScreen"> {
  return {
    EnergySaverLedOff: o.ledOff,
    EnergySaverLowerTemp: o.lowerTemp,
    EnergySaverTempOffset: o.lowerTemp ? o.tempOffset : 0,
    EnergySaverBlackScreen: o.blackScreen,
  };
}

function getTypeSummary(h: OpenHourType): string {
  const label = getTypeLabel(h.Type);
  if (h.Type !== ENERGY_SAVER_TYPE) return label;

  const parts: string[] = [];
  if (h.EnergySaverLedOff ?? true) parts.push("LEDs off");
  if (h.EnergySaverLowerTemp && h.EnergySaverTempOffset) parts.push(`+${h.EnergySaverTempOffset} °C`);
  if (h.EnergySaverBlackScreen) parts.push("black screen");
  return parts.length ? `${label} (${parts.join(", ")})` : label;
}

function EnergySaverFields({ value, onChange }: { value: EnergySaverOptions; onChange: (v: EnergySaverOptions) => void }) {
  return (
    <>
      <p className={styles.hint}>During the window the kiosk saves energy as selected below. Touching the screen turns LEDs and screen back on until the kiosk is idle again.</p>
      <div className={styles.fieldRow}>
        <label>Turn off LEDs</label>
        <input type="checkbox" checked={value.ledOff} onChange={(e) => onChange({ ...value, ledOff: e.target.checked })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Black screen</label>
        <input type="checkbox" checked={value.blackScreen} onChange={(e) => onChange({ ...value, blackScreen: e.target.checked })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Lower cooling</label>
        <input type="checkbox" checked={value.lowerTemp} onChange={(e) => onChange({ ...value, lowerTemp: e.target.checked })} />
      </div>
      {value.lowerTemp && (
        <div className={styles.fieldRow}>
          <label>Raise target by (°C)</label>
          <input
            type="number"
            className={styles.fieldInput}
            min={0.5}
            max={MAX_ENERGY_SAVER_TEMP_OFFSET}
            step={0.5}
            value={value.tempOffset}
            onChange={(e) => onChange({ ...value, tempOffset: Number(e.target.value) })}
            style={{ width: 100 }}
          />
        </div>
      )}
    </>
  );
}

function validateEnergySaver(type: number, o: EnergySaverOptions): string | null {
  if (type !== ENERGY_SAVER_TYPE || !o.lowerTemp) return null;
  if (!(o.tempOffset > 0 && o.tempOffset <= MAX_ENERGY_SAVER_TEMP_OFFSET)) {
    return `Temperature offset must be between 0.5 and ${MAX_ENERGY_SAVER_TEMP_OFFSET} °C`;
  }
  return null;
}

/* ── Issa Suggestions Button + Popup ──────────────────────── */

function IssaSuggestionsButton({ kioskId }: { kioskId: number }) {
  const assistant = useAssistant();
  const [quickChat, setQuickChat] = useState<{ prompt: string; displayPrompt: string } | null>(null);

  const handleClick = useCallback(() => {
    const prompt = `Perform a Strategic Revenue & Waste Optimization Audit for Kiosk ID ${kioskId}.

Operational Logic (SQL Execution Layer):
- Filtering: Scope all data to KioskID = ${kioskId} for the last 60 days.
- Data Aggregation: Perform a SQL-first join between TransactionRow, Product, Stocking, and CurrentInventory.
- Metrics Calculation:
  - Revenue (Excl. VAT): Sum of UnitPriceWithoutTax * Quantity.
  - Sales Cost: Use InternalPurchasePrice. If 0, apply the 50% cost assumption (this must be disclosed in the final answer).
  - Waste Cost: Sum of ItemQuantity (where StockingType = 'Waste') multiplied by the product's cost price.
  - Gross Profit: (Revenue Excl. VAT) - (Sales Cost) - (Waste Cost).
  - Gross Margin %: If apply the 50% cost assumption then this should be 50%, otherwise should be (Gross Profit / Revenue Excl. VAT) * 100.

Analysis Logic (Senior Data Analysis Layer):
- Pricing Strategy: Identify products where volume sensitivity suggests a price adjustment. Suggest increases for high-volume/low-margin items and reductions for high-margin/low-volume 'Star' products.
- Waste-to-Margin Tolerance: Determine if high-margin categories are effectively 'subsidizing' their own waste, or if stock levels should be reduced to protect the bottom line.
- Actionable Improvements: Suggest 2-3 specific operational improvements (e.g., inventory mix changes, specific price points, or restock frequency).

Output Requirements:
- Identification: Refer to all items by Name and ID (e.g., 'Redbull (ID: 123)').
- Formatting: Provide a Markdown table of the top 10 products sorted by Gross Profit (Value) descending. Follow this with a full <table> CSV block for all inventory items.
- Style: Professional, concise, and persuasive. Use bolding for key financial metrics.
- No Tech Talk: Do not mention SQL, Python, or data processing steps.

Output Format Example:
- Table: [Product Name & ID | Revenue (Excl VAT) | Waste Cost | Gross Profit | Gross Margin %]
- Strategic Insights: 2-3 Detailed suggestions.
- Assumption Disclosure: Clearly state if the 50% cost assumption was applied.
- You must use the correct currency taken from the database. Do not assume the currency.`;
    const displayPrompt = `Identify potential revenue drivers for Kiosk ${kioskId} — analyze inventory, waste, pricing and suggest improvements.`;
    setQuickChat({ prompt, displayPrompt });
  }, [kioskId]);

  return (
    <>
      <div style={{ marginBottom: "var(--space-3)" }}>
        <button
          onClick={handleClick}
          style={{
            background: "var(--color-primary)",
            color: "var(--color-primary-foreground)",
            border: "none",
            borderRadius: 10,
            padding: "4px 23px",
            fontSize: "1.07rem",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 8px color-mix(in srgb, var(--color-primary) 35%, transparent)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <img src={assistant.face} alt={assistant.name} style={{ width: 32, height: 32, borderRadius: "50%", marginLeft: -4 }} />
          Ask {assistant.name} for suggestions
        </button>
      </div>
      {quickChat && (
        <IssaQuickChat
          open
          prompt={quickChat.prompt}
          displayPrompt={quickChat.displayPrompt}
          model="default"
          onClose={() => setQuickChat(null)}
        />
      )}
    </>
  );
}

/* ── UPS Power Tab ────────────────────────────────────────── */

const UPS_COLORS = ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#795548", "#607D8B"];

/** dKioskDate is kiosk-local wall clock — same handling as upsPowerPage. */
function formatUpsDateTime(s: string): string {
  return formatWallClock(s) || s;
}

function UpsPowerTab({ kioskId, onReady }: { kioskId: number; onReady?: () => void }) {
  const [data, setData] = useState<UpsStatusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(0);

  useEffect(() => {
    const id = ++loadRef.current;
    setLoading(true);
    setError(null);
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 86400000);
    getUpsStatus(toDateInput(weekAgo), toDateInput(today), [kioskId])
      .then((res) => {
        if (id !== loadRef.current) return;
        if (res.bError) { setError(res.sMessage ?? "Error loading UPS data"); return; }
        setData(res.oData ?? []);
      })
      .catch((err) => { if (id === loadRef.current) setError(String(err)); })
      .finally(() => { if (id === loadRef.current) { setLoading(false); onReady?.(); } });
  }, [kioskId]);

  const chartSeries = useMemo(() => {
    if (data.length === 0) return [] as LineSeries[];
    const sorted = [...data].sort((a, b) => new Date(a.dKioskDate).getTime() - new Date(b.dKioskDate).getTime());
    const dateLabels = sorted.map((d) => {
      const dt = new Date(d.dKioskDate);
      return `${dt.getMonth() + 1}/${dt.getDate()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    });

    const powerSeries: LineSeries = {
      color: UPS_COLORS[0]!,
      width: 2,
      data: sorted.map((d, i) => ({
        label: dateLabels[i]!,
        value: d.bHasPower ? 1 : 0,
        tooltip: `K${d.iKioskID} — ${formatUpsDateTime(d.dKioskDate)}\nPower: ${d.bHasPower ? "ON" : "OFF"}`,
      })),
    };

    const batterySeries: LineSeries = {
      color: UPS_COLORS[1]!,
      width: 1,
      fill: true,
      data: sorted.map((d, i) => {
        const pctMatch = (d.sBatteryLevel ?? "").match(/([\d.]+)/);
        let normalized = 0;
        let displayVal = d.sBatteryLevel ?? "";
        if (pctMatch) {
          const num = parseFloat(pctMatch[1]!);
          normalized = num > 1 ? num / 100 : num;
          displayVal = `${Math.round(normalized * 100)}%`;
        }
        return {
          label: dateLabels[i]!,
          value: normalized,
          tooltip: `K${d.iKioskID} — ${formatUpsDateTime(d.dKioskDate)}\nBattery: ${displayVal}`,
        };
      }),
    };

    return [powerSeries, batterySeries];
  }, [data]);

  if (loading) return <div style={{ padding: 16, color: "var(--color-text-secondary)" }}>Loading UPS data...</div>;
  if (error) return <div style={{ padding: 16, color: "var(--color-error)" }}>{error}</div>;
  if (data.length === 0) return <div style={{ padding: 16, color: "var(--color-text-secondary)" }}>No UPS data found for the last 7 days.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 8 }}>
      <h3 style={{ margin: 0, fontWeight: 600, fontSize: "1rem" }}>Power Status Timeline</h3>
      <div style={{ display: "flex", gap: 16, fontSize: "0.8rem" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 2, backgroundColor: UPS_COLORS[0] }} /> Power (0/1)
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 2, backgroundColor: UPS_COLORS[1] }} /> Battery Level
        </span>
      </div>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <LineChart series={chartSeries} width={Math.max(800, data.length * 12)} height={350} showLabels={false} />
      </div>
      <div style={{ maxHeight: 400, overflowY: "auto", marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
          <thead>
            <tr>
              <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--color-border-default)", background: "var(--color-bg-muted)", fontWeight: 600, position: "sticky", top: 0 }}>Date</th>
              <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--color-border-default)", background: "var(--color-bg-muted)", fontWeight: 600, position: "sticky", top: 0 }}>Power</th>
              <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--color-border-default)", background: "var(--color-bg-muted)", fontWeight: 600, position: "sticky", top: 0 }}>Battery Level</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={i}>
                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--color-border-default)" }}>{formatUpsDateTime(d.dKioskDate)}</td>
                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--color-border-default)", color: d.bHasPower ? "var(--color-success)" : "var(--color-error)", fontWeight: 600 }}>{d.bHasPower ? "ON" : "OFF"}</td>
                <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--color-border-default)" }}>{d.sBatteryLevel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Active Hours Tab ─────────────────────────────────────── */

function ActiveHoursTab({ kioskId }: { kioskId: number }) {
  const [activeHours, setActiveHours] = useState<OpenHourType[]>([]);
  const [availableHours, setAvailableHours] = useState<OpenHourType[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [savingActive, setSavingActive] = useState(false);

  // Create form state
  const [newType, setNewType] = useState(0);
  const [newStatus, setNewStatus] = useState<"active" | "inactive">("active");
  const [newDay, setNewDay] = useState(1);
  const [newFrom, setNewFrom] = useState("00:00");
  const [newTo, setNewTo] = useState("00:00");
  const [newPrio, setNewPrio] = useState(1);
  const [newValidTo, setNewValidTo] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return toDateInput(d);
  });
  const [newEnergySaver, setNewEnergySaver] = useState<EnergySaverOptions>(DEFAULT_ENERGY_SAVER);
  const [creating, setCreating] = useState(false);

  // Edit popup state
  const [editItem, setEditItem] = useState<OpenHourType | null>(null);
  const [editType, setEditType] = useState(0);
  const [editStatus, setEditStatus] = useState<"active" | "inactive">("active");
  const [editDay, setEditDay] = useState(1);
  const [editFrom, setEditFrom] = useState("00:00");
  const [editTo, setEditTo] = useState("00:00");
  const [editPrio, setEditPrio] = useState(1);
  const [editValidTo, setEditValidTo] = useState("");
  const [editDeleted, setEditDeleted] = useState(false);
  const [editEnergySaver, setEditEnergySaver] = useState<EnergySaverOptions>(DEFAULT_ENERGY_SAVER);
  const [editSaving, setEditSaving] = useState(false);

  const loadData = useCallback(() => {
    if (kioskId < 1) return;
    getActiveOpenHours(kioskId).then((r) => {
      if (!r.bError && r.oData) setActiveHours(r.oData);
      else setStatus("Failed to load active hours");
    }).catch(() => setStatus("Failed to load active hours"));

    getAvailableOpenHourTypes(kioskId).then((r) => {
      if (!r.bError && r.oData) setAvailableHours(r.oData);
      else setStatus("Failed to load available hours");
    }).catch(() => setStatus("Failed to load available hours"));
  }, [kioskId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveActive = async () => {
    setSavingActive(true);
    setStatus(null);
    try {
      const r = await saveKioskOpenHour(activeHours, kioskId);
      if (!r.bError && r.oData) {
        setStatus("Active hours saved!");
      } else {
        setStatus(r.sMessage ?? "Could not save active hours");
      }
    } catch {
      setStatus("Could not save active hours");
    } finally {
      setSavingActive(false);
    }
  };

  const moveToActive = (hour: OpenHourType) => {
    setAvailableHours((prev) => prev.filter((h) => h.ID !== hour.ID));
    setActiveHours((prev) => [...prev, hour]);
  };

  const moveToAvailable = (hour: OpenHourType) => {
    setActiveHours((prev) => prev.filter((h) => h.ID !== hour.ID));
    setAvailableHours((prev) => [...prev, hour]);
  };

  const handleCreate = async () => {
    const from = newStatus === "inactive" ? "00:00:00" : newFrom;
    const to = newStatus === "inactive" ? "00:00:00" : newTo;

    if (newStatus === "active" && (from.length !== 5 || to.length !== 5)) {
      setStatus("From and To needs to be set in the format HH:MM (for example 08:00)");
      return;
    }
    const esError = validateEnergySaver(newType, newEnergySaver);
    if (esError) {
      setStatus(esError);
      return;
    }

    setCreating(true);
    setStatus(null);
    const oht: OpenHourType = {
      ID: -1,
      From: from,
      To: to,
      Day: newDay,
      OverridePrio: newPrio === 2 ? 1 : 0,
      IsClosed: newStatus === "inactive",
      ValidTo: newValidTo,
      CreatedBy: -1,
      Type: newType,
      Deleted: false,
      ...energySaverToHour(newType === ENERGY_SAVER_TYPE ? newEnergySaver : DEFAULT_ENERGY_SAVER),
      FromTime: "",
      ToTime: "",
    };
    try {
      const r = await newOpenHourType(oht);
      if (!r.bError && r.oData) {
        setStatus("Open hour created successfully");
        loadData();
      } else {
        setStatus(r.sMessage ?? "Could not create open hour");
      }
    } catch {
      setStatus("Could not create open hour");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (hour: OpenHourType) => {
    setEditItem(hour);
    setEditType(hour.Type);
    setEditStatus(hour.IsClosed ? "inactive" : "active");
    setEditDay(hour.Day);
    setEditFrom(hour.FromTime || "00:00");
    setEditTo(hour.ToTime || "00:00");
    setEditPrio(!hour.OverridePrio ? 1 : 2);
    setEditValidTo(hour.ValidTo ? String(hour.ValidTo).slice(0, 10) : "");
    setEditDeleted(false);
    setEditEnergySaver(energySaverFromHour(hour));
  };

  const handleEditSave = async () => {
    if (editStatus === "active" && (editFrom.length !== 5 || editTo.length !== 5)) {
      setStatus("From and To needs to be set in the format HH:MM (for example 08:00)");
      return;
    }
    if (!editItem) return;
    const esError = validateEnergySaver(editType, editEnergySaver);
    if (esError) {
      setStatus(esError);
      return;
    }

    const from = editStatus === "inactive" ? "00:00:00" : editFrom;
    const to = editStatus === "inactive" ? "00:00:00" : editTo;

    setEditSaving(true);
    setStatus(null);
    const oht: OpenHourType = {
      ID: editItem.ID,
      From: from,
      To: to,
      Day: editDay,
      OverridePrio: editPrio === 2 ? 1 : 0,
      IsClosed: editStatus === "inactive",
      ValidTo: editValidTo,
      CreatedBy: -1,
      Type: editType,
      Deleted: editDeleted,
      ...energySaverToHour(editType === ENERGY_SAVER_TYPE ? editEnergySaver : DEFAULT_ENERGY_SAVER),
      FromTime: editFrom,
      ToTime: editTo,
    };
    try {
      const r = await saveOpenHourType(oht);
      if (!r.bError && r.oData) {
        setStatus("Open hour edited successfully");
        setEditItem(null);
        loadData();
      } else {
        setStatus(r.sMessage ?? "Could not edit open hour");
      }
    } catch {
      setStatus("Could not edit open hour");
    } finally {
      setEditSaving(false);
    }
  };

  if (kioskId < 1) {
    return <div className={styles.hint}>Save the kiosk first to manage active hours.</div>;
  }

  const renderHoursTable = (
    hours: OpenHourType[],
    onClickId: (h: OpenHourType) => void,
    actionLabel: string,
    onAction?: (h: OpenHourType) => void,
  ) => (
    <div style={{ overflowX: "auto" }}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>ID</th>
            <th>From</th>
            <th>To</th>
            <th>Day</th>
            <th>Priority</th>
            <th>Shop</th>
            <th>Valid To</th>
            <th>Type</th>
            {onAction && <th></th>}
          </tr>
        </thead>
        <tbody>
          {hours.length === 0 ? (
            <tr><td colSpan={onAction ? 9 : 8} style={{ textAlign: "center", color: "var(--color-text-secondary)" }}>No items</td></tr>
          ) : (
            hours.map((h) => (
              <tr key={h.ID}>
                <td>
                  <button
                    style={{ background: "none", border: "none", color: "var(--color-primary)", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => onClickId(h)}
                  >
                    {h.ID}
                  </button>
                </td>
                <td>{h.FromTime}</td>
                <td>{h.ToTime}</td>
                <td>{getDayLabel(h.Day)}</td>
                <td>{!h.OverridePrio ? "Normal" : "Medium"}</td>
                <td>{h.IsClosed ? "Inactive" : "Active"}</td>
                <td>{h.ValidTo ? String(h.ValidTo).slice(0, 10) : ""}</td>
                <td>{getTypeSummary(h)}</td>
                {onAction && (
                  <td>
                    <button className={styles.secondaryBtn} style={{ fontSize: "var(--text-xs)", padding: "2px 8px" }} onClick={() => onAction(h)}>
                      {actionLabel}
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.formContainerWide}>
      {/* Part 1: Active Hours */}
      <h3 className={styles.sectionTitle}>Active Hours for Kiosk</h3>
      <p className={styles.hint}>These are the active hours. Press "Save Active List" after changes.</p>
      {renderHoursTable(activeHours, openEdit, "→ Available", moveToAvailable)}
      <div style={{ display: "flex", justifyContent: "center", margin: "var(--space-3) 0" }}>
        <button
          className={styles.secondaryBtn}
          disabled={savingActive}
          onClick={handleSaveActive}
          style={{ background: "var(--color-error)", color: "var(--color-error-foreground)", border: "none" }}
        >
          {savingActive ? "Saving..." : "Save Active List"}
        </button>
      </div>

      {/* Part 2: Available Hours */}
      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Available Hours</h3>
      <p className={styles.hint}>These are the hours you can pick from. Click ID to edit, or move to Active.</p>
      {renderHoursTable(availableHours, openEdit, "→ Active", moveToActive)}

      {/* Part 3: Create New */}
      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Create New Hour Type</h3>
      <p className={styles.hint}>Create a new hour that can be applied to a kiosk.</p>

      <div className={styles.fieldRow}>
        <label>Type</label>
        <select className={styles.fieldSelect} value={newType} onChange={(e) => setNewType(Number(e.target.value))}>
          {TYPE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      {newType === ENERGY_SAVER_TYPE && <EnergySaverFields value={newEnergySaver} onChange={setNewEnergySaver} />}

      <div className={styles.fieldRow}>
        <label>Status</label>
        <select className={styles.fieldSelect} value={newStatus} onChange={(e) => setNewStatus(e.target.value as "active" | "inactive")}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className={styles.fieldRow}>
        <label>Valid To</label>
        <input type="date" className={styles.fieldInput} value={newValidTo} onChange={(e) => setNewValidTo(e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Day</label>
        <select className={styles.fieldSelect} value={newDay} onChange={(e) => setNewDay(Number(e.target.value))}>
          {DAY_OPTIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </div>

      {newStatus === "active" && (
        <>
          <div className={styles.fieldRow}>
            <label>From</label>
            <input className={styles.fieldInput} placeholder="HH:MM" value={newFrom} onChange={(e) => setNewFrom(e.target.value)} style={{ width: 100 }} />
          </div>
          <div className={styles.fieldRow}>
            <label>To</label>
            <input className={styles.fieldInput} placeholder="HH:MM" value={newTo} onChange={(e) => setNewTo(e.target.value)} style={{ width: 100 }} />
          </div>
        </>
      )}

      <div className={styles.fieldRow}>
        <label>Priority</label>
        <select className={styles.fieldSelect} value={newPrio} onChange={(e) => setNewPrio(Number(e.target.value))}>
          {PRIO_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", justifyContent: "center", margin: "var(--space-3) 0" }}>
        <button
          className={styles.secondaryBtn}
          disabled={creating}
          onClick={handleCreate}
          style={{ background: "var(--color-error)", color: "var(--color-error-foreground)", border: "none" }}
        >
          {creating ? "Creating..." : "Create"}
        </button>
      </div>

      {/* Edit Popup */}
      {editItem && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 400,
        }} onClick={() => setEditItem(null)}>
          <div
            style={{
              background: "var(--color-bg)", borderRadius: "var(--radius-lg)",
              padding: "var(--space-5)", minWidth: 400, maxWidth: 500,
              boxShadow: "var(--shadow-lg)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 var(--space-3)" }}>Edit Open Hour (ID: {editItem.ID})</h3>

            <div className={styles.fieldRow}>
              <label>Type</label>
              <select className={styles.fieldSelect} value={editType} onChange={(e) => setEditType(Number(e.target.value))}>
                {TYPE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>

            {editType === ENERGY_SAVER_TYPE && <EnergySaverFields value={editEnergySaver} onChange={setEditEnergySaver} />}

            <div className={styles.fieldRow}>
              <label>Shop</label>
              <select className={styles.fieldSelect} value={editStatus} onChange={(e) => setEditStatus(e.target.value as "active" | "inactive")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className={styles.fieldRow}>
              <label>Priority</label>
              <select className={styles.fieldSelect} value={editPrio} onChange={(e) => setEditPrio(Number(e.target.value))}>
                {PRIO_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>

            <div className={styles.fieldRow}>
              <label>Day</label>
              <select className={styles.fieldSelect} value={editDay} onChange={(e) => setEditDay(Number(e.target.value))}>
                {DAY_OPTIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>

            {editStatus === "active" && (
              <>
                <div className={styles.fieldRow}>
                  <label>From</label>
                  <input className={styles.fieldInput} placeholder="HH:MM" value={editFrom} onChange={(e) => setEditFrom(e.target.value)} style={{ width: 100 }} />
                </div>
                <div className={styles.fieldRow}>
                  <label>To</label>
                  <input className={styles.fieldInput} placeholder="HH:MM" value={editTo} onChange={(e) => setEditTo(e.target.value)} style={{ width: 100 }} />
                </div>
              </>
            )}

            <div className={styles.fieldRow}>
              <label>Valid To</label>
              <input type="date" className={styles.fieldInput} value={editValidTo} onChange={(e) => setEditValidTo(e.target.value)} />
            </div>

            <div className={styles.fieldRow}>
              <label>Delete</label>
              <input type="checkbox" checked={editDeleted} onChange={(e) => setEditDeleted(e.target.checked)} />
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
              <button className={styles.secondaryBtn} onClick={() => setEditItem(null)}>Cancel</button>
              <button
                className={styles.secondaryBtn}
                disabled={editSaving}
                onClick={handleEditSave}
                style={{ background: "var(--color-error)", color: "var(--color-error-foreground)", border: "none" }}
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {status && (
        <div
          className={`${styles.statusMsg} ${status.includes("success") || status.includes("saved") ? styles.statusSuccess : styles.statusError}`}
          style={{ marginTop: "var(--space-2)", textAlign: "center" }}
        >
          {status}
        </div>
      )}
    </div>
  );
}
